import { type DemoRequiredInput, scoreAdjustedNurses, scoreForShiftType } from "./hospitalDemoScore";
import { type CensusGrouping, type DepartmentLocation, NurseFullRoleShort, type NurseRole, type RatioLadder, StatsMode } from "./models";

/**
 * Canonical per-location census and required-amount resolution.
 *
 * ONE answer to "what is this location's census?" and "how many staff does it require?", replacing
 * the five census resolvers and four ladder implementations that previously disagreed on key
 * priority, out-of-range clamping, and fallback behaviour.
 *
 * The Python twin is `backend/util/census_resolution.py`. The two are held to identical semantics by
 * `shared/test-vectors/census_resolution.json`, which both test suites read — a change to one side
 * without the other turns the other's suite red. **Do not change behaviour here without changing the
 * vectors and the Python twin in the same commit.**
 *
 * Per the project rule, nothing in this module has default parameter values or optional parameters:
 * every caller passes every argument explicitly.
 */

/**
 * The value meaning "the EMR reported nothing for this dimension". Opaque and namespaced so that a
 * real unit or floor can never collide with it — a facility genuinely named "Unassigned" would
 * otherwise switch that whole dimension off. The backend produces this token; the UI must never
 * render it raw, and instead maps it to a localized label.
 *
 * Not to be confused with two unrelated sentinels: the backend's `UNASSIGNED_JOB_ID` (the job
 * dimension) and `UNASSIGNED_GROUP_KEY` in useCensusGrouping (nurses with no location).
 */
export const UNASSIGNED_DIMENSION_VALUE = "__IH_Unassigned";

export type CensusDimension = "unit" | "floor" | "room";

export const DIMENSION_UNIT: CensusDimension = "unit";
export const DIMENSION_FLOOR: CensusDimension = "floor";
export const DIMENSION_ROOM: CensusDimension = "room";

/**
 * Canonical order. Key construction, signatures and available-dimension lists all follow it, so the
 * two language implementations cannot disagree on ordering.
 */
const DIMENSION_ORDER: readonly CensusDimension[] = [DIMENSION_UNIT, DIMENSION_FLOOR, DIMENSION_ROOM];

const KEY_SEPARATOR = "|";
const KEY_ASSIGNMENT = "=";

/** Whether a RAW (unescaped) dimension value is the "EMR reported nothing" sentinel. */
export const isUnassignedDimensionValue = (value: string): boolean => value === UNASSIGNED_DIMENSION_VALUE;

/**
 * Escape the three characters that would otherwise break key parsing.
 *
 * `%` must be escaped FIRST — doing it after `|` and `=` would re-escape the `%` in the `%7C`/`%3D`
 * sequences those produce and corrupt the key.
 */
const escapeDimensionValue = (value: string): string => value.replaceAll("%", "%25").replaceAll(KEY_SEPARATOR, "%7C").replaceAll(KEY_ASSIGNMENT, "%3D");

const ESCAPED_UNASSIGNED = escapeDimensionValue(UNASSIGNED_DIMENSION_VALUE);

/**
 * A dimension is configured when it is present and not blank.
 *
 * Accepts `null` as well as `undefined` on purpose: the backend serialises an unset unit/floor as
 * JSON `null`, not as an omitted key, so a `=== undefined` check alone crashes on real payloads. The
 * `DepartmentLocation` type says `string | undefined`, which is inaccurate about the wire — every
 * guard in this module treats null and undefined alike rather than trusting that type.
 */
const hasValue = (value: string | null | undefined): value is string => value !== null && value !== undefined && value.trim() !== "";

/**
 * Build the canonical grouping key for a dimension combination.
 *
 * Dimensions appear in `DIMENSION_ORDER`; absent ones are omitted entirely, so a unit-only key is
 * `unit=2N` rather than `unit=2N|floor=|room=`.
 *
 * Normalisation differs by dimension and is applied here so it happens in exactly one place: unit
 * and floor are trimmed only (they stay CASE-SENSITIVE, matching how they are compared today), while
 * rooms are trimmed and lowercased, preserving the existing `room_name.lower()` matching.
 */
export const normalizeUnitOrFloor = (value: string): string => value.trim();
export const normalizeRoom = (value: string): string => value.trim().toLowerCase();

export const censusDimensionKey = (unit: string | null, floor: string | null, room: string | null): string => {
	const parts: string[] = [];
	if (unit !== null) parts.push(`${DIMENSION_UNIT}${KEY_ASSIGNMENT}${escapeDimensionValue(normalizeUnitOrFloor(unit))}`);
	if (floor !== null) parts.push(`${DIMENSION_FLOOR}${KEY_ASSIGNMENT}${escapeDimensionValue(normalizeUnitOrFloor(floor))}`);
	if (room !== null) parts.push(`${DIMENSION_ROOM}${KEY_ASSIGNMENT}${escapeDimensionValue(normalizeRoom(room))}`);
	return parts.join(KEY_SEPARATOR);
};

/**
 * Which dimensions the day's grouping actually reports, in canonical order.
 *
 * A dimension counts as available when at least one SINGLE-dimension key for it carries a non-zero
 * count under a value that is not the sentinel.
 *
 * This gate is what makes strict key matching safe. Only PCC populates floor data; Matrix and
 * Reliable Health bucket every patient under the sentinel, so without the gate every location
 * configured with a floor in those facilities would resolve to zero census. It also covers
 * manually-entered census: if an admin typed unit figures only, floor is simply not available that
 * day and unit+floor locations fall back to a unit lookup — as a deliberate, department-wide
 * decision rather than a per-location guess.
 */
export const availableCensusDimensions = (byDimensions: Record<string, number>): readonly CensusDimension[] => {
	const available: CensusDimension[] = [];
	for (const dimension of DIMENSION_ORDER) {
		const prefix = `${dimension}${KEY_ASSIGNMENT}`;
		for (const [key, count] of Object.entries(byDimensions)) {
			if (!key.startsWith(prefix)) continue;
			if (key.includes(KEY_SEPARATOR)) continue; // composite key; only single-dimension keys establish availability
			if (count <= 0) continue;
			if (key.slice(prefix.length) === ESCAPED_UNASSIGNED) continue;
			available.push(dimension);
			break;
		}
	}
	return available;
};

// The canonical dimension map for a day. A thin accessor rather than a raw field read: it absorbs a
// missing grouping so every call site gets the same empty-map contract instead of each one guarding.
//
// DEFAULT: a day with no `census_grouping` at all (no EMR feed, no manual entry) reads as an empty
// map rather than throwing. That is the correct contract here — every downstream lookup already
// treats a missing key as zero patients, so an empty map and a map full of misses mean the same
// thing. Absorbing it once is what stops each call site from inventing its own guard.
export const byDimensionsOf = (grouping: CensusGrouping | undefined): Record<string, number> => grouping?.by_dimensions ?? {};

// DEFAULT: rooms are hydrated by a separate query on the backend, so a payload from an endpoint that
// does not join them omits the field. Empty means "not mapped to any room", which drops ROOM from the
// configured signature — the same answer as a location that genuinely has no rooms.
const locationRooms = (location: DepartmentLocation): readonly { room_name: string }[] => location.rooms ?? [];

// DEFAULT: same shape as rooms — the ladder is a separate join and is absent on payloads that do not
// include it. Empty means no eligible rung, which `requiredAmountForLocation` answers with 0, never
// with the configured amount. Deliberate: a missing ladder must not silently staff a ratio location.
const locationLadder = (location: DepartmentLocation): readonly RatioLadder[] => location.ratioLadder ?? [];

/** The dimensions this location is mapped to, in canonical order. */
export const configuredCensusDimensions = (location: DepartmentLocation): readonly CensusDimension[] => {
	const configured: CensusDimension[] = [];
	if (hasValue(location.unit)) configured.push(DIMENSION_UNIT);
	if (hasValue(location.floor)) configured.push(DIMENSION_FLOOR);
	if (locationRooms(location).length > 0) configured.push(DIMENSION_ROOM);
	return configured;
};

/** The dimensions this location will actually be looked up by: configured AND reported. */
export const locationDimensionSignature = (location: DepartmentLocation, availableDimensions: readonly CensusDimension[]): readonly CensusDimension[] => {
	const available = new Set(availableDimensions);
	return configuredCensusDimensions(location).filter((dimension) => available.has(dimension));
};

/**
 * A key with no entry means zero patients — a real answer, not a missing one.
 *
 * Written as an explicit membership test rather than `?? 0` so that "miss is zero" reads as the
 * deliberate decision it is.
 */
const lookup = (byDimensions: Record<string, number>, key: string): number => {
	if (!Object.hasOwn(byDimensions, key)) return 0;
	return byDimensions[key];
};

/**
 * The census a location's staffing should be computed against.
 *
 * A location is looked up by an exact dimension key, and a missing key is zero. There is never a
 * cross-dimension fallback to a *different* bucket: silently reading a broader slice than configured
 * is the class of bug this replaces.
 *
 * A location with NO unit/floor/room mapping is the one exception, and it is not a fallback: it names
 * no slice, so the department total is its census — in every stats mode. Distinct from a MAPPED
 * location whose dimensions the day cannot resolve, which stays 0.
 *
 * `allowAvailabilityFallback` selects between two readings of "exact":
 *
 * - `false` — the location's FULL configured conjunction, always. A location mapped to unit+floor is
 *   looked up only by `unit=U|floor=F`; if the feed does not report floors that key is absent and the
 *   answer is 0. Strictest and most literal: the number can only ever come from the precise slice the
 *   location claims to cover.
 * - `true` — narrow the lookup to the configured dimensions the day's feed actually reports (the
 *   availability gate). The same unit+floor location degrades to `unit=U` on a day with no floor data,
 *   which keeps Matrix / Reliable Health and unit-only manual census working, at the cost of reading a
 *   coarser bucket than the location is mapped to.
 *
 * Both modes agree whenever the feed reports every dimension the location is configured with, which is
 * the common case; they diverge only on partially-reported days.
 */
/**
 * The value every production caller passes for `resolveLocationCensus`'s `allowAvailabilityFallback`
 * — currently OFF, so a location resolves only by its full configured conjunction. Named rather than
 * written as a bare `false` at eight call sites so the policy has one flip point and one explanation.
 *
 * This is NOT a default: the parameter stays required, so a new call site must still state a value.
 * Flipping it to `true` restores the availability gate (a unit+floor location degrading to a unit
 * lookup on a day with no floor data), which Matrix / Reliable Health and unit-only manual census
 * rely on — so measure before flipping. The backend twin is `ALLOW_AVAILABILITY_FALLBACK` in
 * `util/census_resolution.py`; change both together or the UI and the recommendations engine diverge.
 */
export const ALLOW_AVAILABILITY_FALLBACK = false;

/**
 * The stored `censusEnd` of a ratio-ladder row whose Max is "no upper limit". The column is an integer,
 * so ∞ is saved as a value no real census reaches and shown as "∞" everywhere a range is displayed.
 * The bracket math needs nothing special for it. The backend twin is `RATIO_LADDER_UNBOUNDED_CENSUS_END`
 * in `util/census_resolution.py`; both are pinned to the shared vectors.
 */
export const RATIO_LADDER_UNBOUNDED_CENSUS_END = 99999;

/** How a ladder range bound reads on screen: the unbounded end as "∞", anything else as its number. */
export const censusBoundLabel = (value: number): string => (value === RATIO_LADDER_UNBOUNDED_CENSUS_END ? "∞" : String(value));

export const resolveLocationCensus = (
	location: DepartmentLocation,
	byDimensions: Record<string, number>,
	totalCensus: number,
	// `statsMode` no longer changes the census in either language — it survives for signature parity with the Python twin, which
	// still reads it to decide whether an unresolvable location is worth a WARNING. Dropping it here alone would let a caller pass
	// the two functions different arguments in the same order and be told nothing; dropping it on both sides would cost the backend
	// its only signal that a ratio department's feed stopped reporting an axis.
	// biome-ignore lint/correctness/noUnusedFunctionParameters: signature parity with the Python twin — see above
	statsMode: StatsMode,
	allowAvailabilityFallback: boolean,
): number => {
	const configured = configuredCensusDimensions(location);

	if (configured.length === 0) {
		// An unmapped location names no slice of the building, so the only census it can be about is the
		// whole department's — in EVERY stats mode. A location with no unit/floor/room is the ordinary
		// shape of a department that covers one area and never subdivided it, so the ladder is meant to
		// run against the department total; returning 0 instead muted those ladders entirely, which
		// removed deficits and suppressed the call-off replacement search.
		//
		// The cost is real and accepted: a ratio department with SEVERAL unmapped locations gives each of
		// them the full department census, so their ladders over-count. Mapping is what makes that
		// answerable, and the total is the honest answer to "what census does this location cover?" when
		// it claims to cover everything.
		//
		// Deliberately NOT governed by allowAvailabilityFallback: that flag chooses which key a MAPPED
		// location is read by, whereas this branch is about a location that names no dimension at all.
		//
		// DEFAULT: the department total stands in for a location that names no slice. Flagged because it
		// is a substituted value rather than a measured one: in a ratio department with SEVERAL unmapped
		// locations every one of them reads the full department census and its ladder over-counts in
		// proportion to how many there are. Accepted over returning 0, which muted those ladders entirely.
		return totalCensus;
	}

	const signature = allowAvailabilityFallback ? locationDimensionSignature(location, availableCensusDimensions(byDimensions)) : configured;
	if (signature.length === 0) {
		// Reachable only under allowAvailabilityFallback: mapped, but to dimensions this EMR does not
		// report, so the gate narrowed the signature to nothing. Zero regardless of stats mode — unlike
		// the unmapped case above, the intent here is known and simply unsatisfiable today. In strict
		// mode `signature === configured`, which the guard above already proved non-empty.
		return 0;
	}

	const unit = signature.includes(DIMENSION_UNIT) && hasValue(location.unit) ? location.unit : null;
	const floor = signature.includes(DIMENSION_FLOOR) && hasValue(location.floor) ? location.floor : null;

	if (signature.includes(DIMENSION_ROOM)) {
		// A patient occupies exactly one room, so summing a location's rooms cannot double-count.
		return locationRooms(location).reduce((sum, room) => sum + lookup(byDimensions, censusDimensionKey(unit, floor, room.room_name)), 0);
	}

	return lookup(byDimensions, censusDimensionKey(unit, floor, null));
};

/**
 * Which staffing dimension a required-amount question is about. A required, total choice: previously
 * this was an optional argument whose absence was indistinguishable from "no filtering", with an
 * implicit job-beats-roles cascade. Now every caller states what it is asking for.
 */
export type LadderFilter = { kind: "all" } | { kind: "roles"; roles: NurseRole[] } | { kind: "job"; jobId: string };

const rungMatchesFilter = (rung: RatioLadder, ladderFilter: LadderFilter): boolean => {
	switch (ladderFilter.kind) {
		case "all":
			return true;
		case "roles":
			return rung.roles.some((role) => ladderFilter.roles.includes(role));
		case "job":
			return rung.jobId === ladderFilter.jobId;
	}
};

/**
 * A rung with no `days` applies to every weekday, matching the backend model default of all seven.
 * An explicitly empty list applies to none.
 */
const rungAppliesOnDay = (rung: RatioLadder, day: string): boolean => rung.days == null || rung.days.includes(day);

/**
 * The location's CONFIGURED requirement for a weekday, ignoring census and any ratio ladder.
 *
 * This is the PPD/HPPD answer, and also what a surface wants when it is deliberately census-blind —
 * Synergy staffing shows configured targets, not census-derived ones. Exposed as its own function so
 * those callers say what they mean, rather than passing an undefined census and relying on the
 * resolver to fall through.
 */
export const configuredAmountRequired = (location: DepartmentLocation, day: string): number => {
	const dayAmount = location.dayAmounts?.[day];
	if (dayAmount != null) return dayAmount.amount_required;
	// DEFAULT: no entry for this weekday falls back to location.amountRequired, which the backend
	// computes as the max amount_required across the days that ARE configured. Preserved from the
	// previous behaviour rather than changed here, but flagged because it means an unconfigured day
	// inherits the busiest configured day's requirement.
	return location.amountRequired;
};

/**
 * How many staff this location requires for `day` at `census`.
 *
 * Ratio departments are governed by the ratio ladder and ONLY the ratio ladder — when no rung is
 * eligible (no ladder, the day is disabled, or every rung belongs to another dimension) the answer is
 * 0, never the configured amount. Falling back would hide ladder-configuration gaps behind a
 * plausible number, which is exactly what made the previous implementations disagree.
 *
 * PPD/HPPD departments ignore the ladder entirely. Rungs survive on PPD locations because the editor
 * only hides the ladder UI rather than deleting rows, so an orphaned ladder must not drive staffing.
 */
export const requiredAmountForLocation = (
	location: DepartmentLocation,
	census: number,
	statsMode: StatsMode,
	day: string,
	ladderFilter: LadderFilter,
): number => {
	if (statsMode === StatsMode.PPD_HPPD) return configuredAmountRequired(location, day);

	const eligible = locationLadder(location).filter((rung) => rungAppliesOnDay(rung, day) && rungMatchesFilter(rung, ladderFilter));
	if (eligible.length === 0) return 0;

	let matchedTotal = 0;
	let hasMatch = false;
	let highestRung = eligible[0];
	let nearestLower: RatioLadder | undefined;

	for (const rung of eligible) {
		if (rung.censusEnd > highestRung.censusEnd) highestRung = rung;
		if (census >= rung.censusStart && census <= rung.censusEnd) {
			// Brackets are mutually exclusive within a dimension, so summing across dimensions gives
			// the location total without double-counting.
			matchedTotal += rung.amount;
			hasMatch = true;
		} else if (rung.censusEnd < census && (nearestLower === undefined || rung.censusEnd > nearestLower.censusEnd)) {
			nearestLower = rung;
		}
	}

	if (hasMatch) return matchedTotal;
	if (census > highestRung.censusEnd) return highestRung.amount; // above the top bracket clamps to it rather than collapsing to 0
	if (nearestLower !== undefined) return nearestLower.amount; // a gap between brackets takes the nearest lower one
	return 0; // below the lowest bracket the ladder genuinely requires nobody
};

/**
 * Whether a whole location (not a rung) belongs to the dimension a filter asks about. Used for
 * node-split numbers, which are per location and have no rungs to filter.
 */
const locationMatchesFilter = (location: DepartmentLocation, ladderFilter: LadderFilter): boolean => {
	switch (ladderFilter.kind) {
		case "all":
			return true;
		case "roles": {
			// The filter holds short role codes ("RN"); the location holds full role names.
			const filterRoles = new Set<string>(ladderFilter.roles);
			return location.roles.some((role) => role === "all" || filterRoles.has(NurseFullRoleShort[role]));
		}
		case "job":
			return location.jobId === ladderFilter.jobId;
	}
};

/**
 * One location's required nurse count under a ladder filter, adjusted for demo departments.
 *
 * The single unit the location picker, Day View gaps, Weekly gaps, the staff table and the daily
 * printout all need, so the number can never drift between them:
 * - a `node` demo takes the location's share of the node (0 when the filter is about another
 *   role/job). A location the node does not cover falls through to the score/ladder path below.
 * - otherwise `requiredAmountForLocation` gives the ladder answer and `scoreForShiftType` the
 *   (hospital-demo-only) multiplier.
 *
 * The demo paths are front-end only and not part of the Python twin's semantics.
 */
export const scoreAdjustedRequiredForLocation = (
	location: DepartmentLocation,
	census: number,
	statsMode: StatsMode,
	day: string,
	ladderFilter: LadderFilter,
	demoRequired: DemoRequiredInput,
): number => {
	if (demoRequired?.kind === "node") {
		const nodeShare = demoRequired.requiredByLocationId.get(location.id);
		if (nodeShare !== undefined) return locationMatchesFilter(location, ladderFilter) ? nodeShare : 0;
	}
	const scoreByShiftType = demoRequired === null ? null : demoRequired.scoreByShiftType;
	return scoreAdjustedNurses(
		requiredAmountForLocation(location, census, statsMode, day, ladderFilter),
		scoreForShiftType(scoreByShiftType, location.shift_type),
	);
};
